import styled from 'styled-components';

interface SidebarProps {
  isOpen: boolean;
}

const TRANSITION_DURATION = 300;

export const Container = styled.div<SidebarProps>`
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  height: 100vh;
  background-color: ${(props: any) => props.theme.colors.interfacePure0};
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  transform: ${({ isOpen }) => (isOpen ? 'translateX(0)' : 'translateX(100%)')};
  transition: transform ${TRANSITION_DURATION}ms ease;
  border-left: 1px solid ${(props: any) => props.theme.colors.ce5e7eb};

  /* Tablet breakpoint */
  @media (max-width: 1024px) {
    width: 380px;
  }

  /* Mobile breakpoint */
  @media (max-width: 768px) {
    width: 100vw;
    height: 100vh;
    height: 100dvh; /* Dynamic viewport height for mobile browsers */
    box-shadow: none;
    border-left: none;
  }

  /* Small mobile breakpoint */
  @media (max-width: 480px) {
    width: 100vw;
  }
`;

export const SidebarContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${(props: any) => props.theme.colors.ce5e7eb};
  background-color: ${(props: any) => props.theme.colors.interfacePure0};
  flex-shrink: 0;

  /* Mobile adjustments */
  @media (max-width: 768px) {
    padding: 16px 20px;
    min-height: 60px;
  }

  @media (max-width: 480px) {
    padding: 12px 16px;
    flex-wrap: wrap;
    gap: 8px;
  }
`;

export const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 480px) {
    flex: 1;
  }
`;

export const Title = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: ${(props: any) => props.theme.colors.c1f2937};

  /* Mobile adjustments */
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

export const ButtonSection = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  @media (max-width: 480px) {
    flex-shrink: 0;
  }
`;

export const ClearButton = styled.button`
  padding: 8px 16px;
  border: 1px solid ${(props: any) => props.theme.colors.cd1d5db};
  background-color: transparent;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${(props: any) => props.theme.colors.c374151};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: ${(props: any) => props.theme.colors.cf9fafb};
    border-color: ${(props: any) => props.theme.colors.c9ca3af};
  }

  &:active {
    background-color: ${(props: any) => props.theme.colors.cf3f4f6};
  }

  /* Mobile adjustments */
  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 0.875rem;
    min-height: 40px;
  }

  @media (max-width: 480px) {
    padding: 8px 12px;
    font-size: 0.8rem;
    min-height: 36px;
  }
`;

export const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background-color: transparent;
  border-radius: 6px;
  cursor: pointer;
  color: ${(props: any) => props.theme.colors.c6b7280};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props: any) => props.theme.colors.cf3f4f6};
    color: ${(props: any) => props.theme.colors.c374151};
  }

  /* Mobile adjustments */
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    
    svg {
      width: 22px;
      height: 22px;
    }
  }

  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

export const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px 24px 24px 24px;

  /* Mobile adjustments */
  @media (max-width: 768px) {
    padding: 20px;
    /* Ensure smooth scrolling on mobile */
    -webkit-overflow-scrolling: touch;
  }

  @media (max-width: 480px) {
    padding: 16px;
  }

  /* Scrollbar customizado */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${(props: any) => props.theme.colors.cf1f5f9};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props: any) => props.theme.colors.ccbd5e1};
    border-radius: 3px;

    &:hover {
      background: ${(props: any) => props.theme.colors.c94a3b8};
    }
  }

  /* Para Firefox */
  scrollbar-width: thin;
  scrollbar-color: ${(props: any) => props.theme.colors.ccbd5e1} ${(props: any) => props.theme.colors.cf1f5f9};

  /* Hide scrollbar on mobile for cleaner look */
  @media (max-width: 768px) {
    &::-webkit-scrollbar {
      width: 0px;
      background: transparent;
    }
    
    scrollbar-width: none;
  }
`;

export const Overlay = styled.div<SidebarProps>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  transition: opacity ${TRANSITION_DURATION}ms ease, visibility ${TRANSITION_DURATION}ms ease;

  /* Hide overlay on desktop - only show on mobile */
  @media (min-width: 769px) {
    display: none;
  }

  @media (max-width: 768px) {
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
  }
`;
